import React, { PureComponent } from 'react';
import { findDOMNode } from 'react-dom';
import classNames from 'classnames';
import animation from '../_util/openAnimation';
import warning from '../_util/warning';
import SubMenu from './SubMenu';
import Item from './MenuItem';
import RcMenu, { Divider, ItemGroup } from '../rc-components/menu';
import LayoutSiderContext from '../layout/LayoutSiderContext';
import { MenuContextProvider } from './MenuContext';
export default class Menu extends PureComponent {
    constructor(props, context) {
        super(props, context);
        this.inlineOpenKeys = [];
        this.handleClick = (e) => {
            this.handleOpenChange([]);
            const { onClick } = this.props;
            if (onClick) {
                onClick(e);
            }
        };
        this.handleOpenChange = (openKeys) => {
            this.setOpenKeys(openKeys);
            const { onOpenChange } = this.props;
            if (onOpenChange) {
                onOpenChange(openKeys);
            }
        };
        warning(!('onOpen' in props || 'onClose' in props), '`onOpen` and `onClose` are removed, please use `onOpenChange` instead');
        warning(!('inlineCollapsed' in props && props.mode !== 'inline'), '`inlineCollapsed` should only be used when Menu\'s `mode` is inline.');
        let openKeys;
        if ('defaultOpenKeys' in props) {
            openKeys = props.defaultOpenKeys;
        }
        else if ('openKeys' in props) {
            openKeys = props.openKeys;
        }
        this.state = {
            openKeys: openKeys || [],
        };
    }
    static get contextType() {
        return LayoutSiderContext;
    }
    getContextValue() {
        const { theme } = this.props;
        return {
            inlineCollapsed: this.getInlineCollapsed(),
            menuTheme: theme,
        };
    }
    getPrefixCls() {
        const { prefixCls } = this.props;
        const { getPrefixCls } = this.context;
        return getPrefixCls('menu', prefixCls);
    }
    componentWillReceiveProps(nextProps, nextContext) {
        const { mode, inlineCollapsed } = this.props;
        if (mode === 'inline' && nextProps.mode !== 'inline') {
            this.switchModeFromInline = true;
        }
        if ('openKeys' in nextProps) {
            this.setState({ openKeys: nextProps.openKeys });
            return;
        }
        const { openKeys } = this.state;
        const { siderCollapsed } = this.context;
        if ((nextProps.inlineCollapsed && !inlineCollapsed) ||
            (nextContext.siderCollapsed && !siderCollapsed)) {
            this.switchModeFromInline =
                !!openKeys.length &&
                    !!findDOMNode(this).querySelectorAll(`.${this.getPrefixCls()}-submenu-open`).length;
            this.inlineOpenKeys = openKeys;
            this.setState({ openKeys: [] });
        }
        if ((!nextProps.inlineCollapsed && inlineCollapsed) ||
            (!nextContext.siderCollapsed && siderCollapsed)) {
            this.setState({ openKeys: this.inlineOpenKeys });
            this.inlineOpenKeys = [];
        }
    }
    setOpenKeys(openKeys) {
        if (!('openKeys' in this.props)) {
            this.setState({ openKeys });
        }
    }
    getRealMenuMode() {
        const inlineCollapsed = this.getInlineCollapsed();
        if (this.switchModeFromInline && inlineCollapsed) {
            return 'inline';
        }
        const { mode } = this.props;
        return inlineCollapsed ? 'vertical' : mode;
    }
    getInlineCollapsed() {
        const { inlineCollapsed } = this.props;
        const { siderCollapsed = inlineCollapsed } = this.context;
        return siderCollapsed;
    }
    getMenuOpenAnimation(menuMode) {
        const { openAnimation, openTransitionName } = this.props;
        let menuOpenAnimation = openAnimation || openTransitionName;
        if (openAnimation === undefined && openTransitionName === undefined) {
            switch (menuMode) {
                case 'horizontal':
                    menuOpenAnimation = 'slide-up';
                    break;
                case 'vertical':
                case 'vertical-left':
                case 'vertical-right':
                    // When mode switch from inline
                    // submenu should hide without animation
                    if (this.switchModeFromInline) {
                        menuOpenAnimation = '';
                        this.switchModeFromInline = false;
                    }
                    else {
                        menuOpenAnimation = 'zoom-big';
                    }
                    break;
                case 'inline':
                    menuOpenAnimation = {
                        ...animation,
                        leave: (node, done) => animation.leave(node, () => {
                            // Make sure inline menu leave animation finished before mode is switched
                            this.switchModeFromInline = false;
                            this.setState({});
                            // when inlineCollapsed change false to true, all submenu will be unmounted,
                            // so that we don't need handle animation leaving.
                            if (this.getRealMenuMode() === 'vertical') {
                                return;
                            }
                            done();
                        }),
                    };
                    break;
                default:
            }
        }
        return menuOpenAnimation;
    }
    render() {
        const { className, theme } = this.props;
        const { openKeys } = this.state;
        const prefixCls = this.getPrefixCls();
        const menuMode = this.getRealMenuMode();
        const menuOpenAnimation = this.getMenuOpenAnimation(menuMode);
        const menuClassName = classNames(className, `${prefixCls}-${theme}`, {
            [`${prefixCls}-inline-collapsed`]: this.getInlineCollapsed(),
        });
        const menuProps = {
            openKeys,
            onOpenChange: this.handleOpenChange,
            className: menuClassName,
            mode: menuMode,
        };
        if (menuMode !== 'inline') {
            // closing vertical popup submenu after click it
            menuProps.onClick = this.handleClick;
            menuProps.openTransitionName = menuOpenAnimation;
        }
        else {
            menuProps.openAnimation = menuOpenAnimation;
        }
        const { collapsedWidth } = this.context;
        if (this.getInlineCollapsed() &&
            (collapsedWidth === 0 || collapsedWidth === '0' || collapsedWidth === '0px')) {
            return null;
        }
        return (React.createElement(MenuContextProvider, Object.assign({}, this.getContextValue()),
            React.createElement(RcMenu, Object.assign({}, this.props, menuProps, { prefixCls: prefixCls }))));
    }
}
Menu.displayName = 'Menu';
Menu.Divider = Divider;
Menu.Item = Item;
Menu.SubMenu = SubMenu;
Menu.ItemGroup = ItemGroup;
Menu.defaultProps = {
    className: '',
    theme: 'light',
    focusable: false,
};
